import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Activity, ArrowLeft, Calendar, User, Clock, Download, Edit2, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import LessonCard from "@/components/pilates/LessonCard";
import ExerciseList from "@/components/pilates/ExerciseList";
import PlanEditModal from "@/components/pilates/PlanEditModal";

interface LessonPlan {
  id: string;
  patient_id: string;
  total_lessons: number;
  frequency: string;
  start_date: string;
  status: string;
  notes: string | null;
  created_at: string;
  patient_snapshot: any;
  ai_recommendations: any;
  patients_onboarding?: {
    full_name: string;
    avatar_url: string | null;
  };
}

interface Exercise {
  id: string;
  lesson_number: number;
  day_of_week: string;
  scheduled_date: string;
  is_completed: boolean;
  exercise_name: string;
  equipment: string | null;
  sets: number | null;
  reps: string | null;
  springs: string | null;
  focus_area: string | null;
  difficulty: string;
  notes: string | null;
  ai_reasoning: string | null;
  display_order: number;
}

interface GroupedLesson {
  lessonNumber: number;
  dayOfWeek: string;
  scheduledDate: string;
  isCompleted: boolean;
  exercises: Exercise[];
}

export default function PilatesPlanDetail() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState<LessonPlan | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [groupedLessons, setGroupedLessons] = useState<GroupedLesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLesson, setSelectedLesson] = useState<GroupedLesson | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    loadPlanDetails();
  }, [planId]);

  const loadPlanDetails = async () => {
    if (!planId) return;

    try {
      setLoading(true);

      // Carregar plano
      const { data: planData, error: planError } = await supabase
        .from("pilates_lesson_plans")
        .select("*, patients_onboarding(full_name, avatar_url)")
        .eq("id", planId)
        .single();

      if (planError) throw planError;
      setPlan(planData);

      // Carregar exercícios
      const { data: exercisesData, error: exercisesError } = await supabase
        .from("pilates_exercises")
        .select("*")
        .eq("lesson_plan_id", planId)
        .order("lesson_number")
        .order("display_order");

      if (exercisesError) throw exercisesError;
      setExercises(exercisesData || []);

      // Agrupar por aula
      const grouped = groupExercisesByLesson(exercisesData || []);
      setGroupedLessons(grouped);
    } catch (error) {
      console.error("Erro ao carregar plano:", error);
      toast.error("Erro ao carregar detalhes do plano");
    } finally {
      setLoading(false);
    }
  };

  const groupExercisesByLesson = (allExercises: Exercise[]): GroupedLesson[] => {
    const lessonsMap = new Map<number, GroupedLesson>();

    allExercises.forEach((exercise) => {
      if (!lessonsMap.has(exercise.lesson_number)) {
        lessonsMap.set(exercise.lesson_number, {
          lessonNumber: exercise.lesson_number,
          dayOfWeek: exercise.day_of_week,
          scheduledDate: exercise.scheduled_date,
          isCompleted: exercise.is_completed,
          exercises: [],
        });
      }
      lessonsMap.get(exercise.lesson_number)!.exercises.push(exercise);
    });

    return Array.from(lessonsMap.values()).sort((a, b) => a.lessonNumber - b.lessonNumber);
  };

  const handleMarkComplete = async (lessonNumber: number) => {
    if (!planId) return;

    try {
      const { error } = await supabase
        .from("pilates_exercises")
        .update({ is_completed: true, completed_at: new Date().toISOString() })
        .eq("lesson_plan_id", planId)
        .eq("lesson_number", lessonNumber);

      if (error) throw error;

      toast.success("Aula marcada como realizada!");
      await loadPlanDetails();
    } catch (error) {
      console.error("Erro ao marcar aula:", error);
      toast.error("Erro ao marcar aula como realizada");
    }
  };

  const handleSavePlan = async (planId: string, data: any) => {
    try {
      const { error } = await supabase
        .from("pilates_lesson_plans")
        .update(data)
        .eq("id", planId);

      if (error) throw error;

      toast.success("Plano atualizado com sucesso!");
      await loadPlanDetails();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Erro ao atualizar plano:", error);
      toast.error("Erro ao atualizar plano");
    }
  };

  const handleDeletePlan = async () => {
    if (!planId) return;

    try {
      // 1. Excluir exercícios do plano
      const { error: exercisesError } = await supabase
        .from("pilates_exercises")
        .delete()
        .eq("lesson_plan_id", planId);

      if (exercisesError) throw exercisesError;

      // ✅ REMOVIDO: patient_health_records não é mais usado
      const recordsError = null;

      if (recordsError) throw recordsError;

      // 3. Excluir o plano
      const { error: planError } = await supabase
        .from("pilates_lesson_plans")
        .delete()
        .eq("id", planId);

      if (planError) throw planError;

      toast.success("Plano excluído com sucesso!");
      navigate("/app/aplicativo/pilatesplanner");
    } catch (error) {
      console.error("Erro ao excluir plano:", error);
      toast.error("Erro ao excluir plano");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-muted-foreground">Plano não encontrado</p>
        <Button onClick={() => navigate("/app/aplicativo/pilatesplanner")}>
          Voltar
        </Button>
      </div>
    );
  }

  const completedLessons = groupedLessons.filter((l) => l.isCompleted).length;
  const progressPercentage = (completedLessons / plan.total_lessons) * 100;

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/app/aplicativo/pilatesplanner")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-pink-100 to-rose-100 dark:from-pink-900/30 dark:to-rose-900/30">
              <Activity className="h-6 w-6 text-pink-600 dark:text-pink-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                Plano de Pilates
              </h1>
              <p className="text-muted-foreground">
                {plan.patients_onboarding?.full_name}
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditModalOpen(true)}>
            <Edit2 className="mr-2 h-4 w-4" />
            Editar Plano
          </Button>
          <Button
            variant="outline"
            className="text-destructive border-destructive hover:bg-destructive/10"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Excluir
          </Button>
          <Button variant="outline" disabled>
            <Download className="mr-2 h-4 w-4" />
            Exportar PDF
          </Button>
        </div>
      </div>

      <Separator />

      {/* Informações do Plano */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-pink-500" />
              Informações do Aluno
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-muted-foreground">Nome</p>
              <p className="font-medium">{plan.patients_onboarding?.full_name}</p>
            </div>
            {plan.patient_snapshot?.age && (
              <div>
                <p className="text-sm text-muted-foreground">Idade</p>
                <p className="font-medium">{plan.patient_snapshot.age} anos</p>
              </div>
            )}
            {plan.patient_snapshot?.conditions && (
              <div>
                <p className="text-sm text-muted-foreground">Condições</p>
                <p className="font-medium text-sm">{plan.patient_snapshot.conditions}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-pink-500" />
              Configuração do Plano
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-muted-foreground">Total de Aulas</p>
              <p className="font-medium">{plan.total_lessons} aulas</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Frequência</p>
              <p className="font-medium">{plan.frequency} por semana</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Data de Início</p>
              <p className="font-medium">
                {new Date(plan.start_date).toLocaleDateString("pt-BR")}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Progresso</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-pink-500 to-rose-500 transition-all"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <span className="text-sm font-medium">
                  {completedLessons}/{plan.total_lessons}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resumo da IA */}
      {plan.ai_recommendations?.summary && (
        <Card className="border-pink-200 dark:border-pink-800">
          <CardHeader>
            <CardTitle className="text-pink-600 dark:text-pink-400">
              Resumo do Plano (IA)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">{plan.ai_recommendations.summary}</p>

            {plan.ai_recommendations?.precautions && plan.ai_recommendations.precautions.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">Precauções:</p>
                <ul className="list-disc list-inside space-y-1">
                  {plan.ai_recommendations.precautions.map((precaution: string, idx: number) => (
                    <li key={idx} className="text-sm text-muted-foreground">{precaution}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Aulas */}
      <div>
        <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5 text-pink-500" />
          Aulas Programadas
        </h2>

        <div className="space-y-4">
          {groupedLessons.map((lesson) => (
            <div key={lesson.lessonNumber}>
              <LessonCard
                lessonNumber={lesson.lessonNumber}
                dayOfWeek={lesson.dayOfWeek}
                scheduledDate={lesson.scheduledDate}
                exerciseCount={lesson.exercises.length}
                isCompleted={lesson.isCompleted}
                onClick={() => setSelectedLesson(lesson)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Modal de Exercícios */}
      {selectedLesson && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>
                    Aula {selectedLesson.lessonNumber} · {selectedLesson.dayOfWeek}
                  </CardTitle>
                  <CardDescription>
                    {new Date(selectedLesson.scheduledDate).toLocaleDateString("pt-BR")}
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedLesson(null)}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-6">
              <ExerciseList
                exercises={selectedLesson.exercises}
                onEdit={() => { }}
                onDelete={() => { }}
              />

              {!selectedLesson.isCompleted && (
                <div className="mt-6 pt-6 border-t">
                  <Button
                    onClick={async () => {
                      await handleMarkComplete(selectedLesson.lessonNumber);
                      setSelectedLesson(null);
                    }}
                    className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
                  >
                    Marcar Aula como Realizada
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de edição */}
      {plan && (
        <PlanEditModal
          plan={plan}
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={handleSavePlan}
        />
      )}

      {/* Diálogo de confirmação de exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão do plano</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação irá excluir permanentemente o plano de aulas, incluindo todas as aulas e
              exercícios associados. Os registros na timeline do paciente também serão removidos.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePlan}
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir Plano
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
